import { AxiosResponse } from 'axios';
import { throttle } from 'src/config';
import { wait } from 'src/utils/wait';
import type { AddIssue, Issue, IssueModelShort, UpdateIssue } from '../interfaces/masters/issue/issue';
import type {
  AddProductIssue,
  ProductIssue,
  ProductIssueMasterView,
  BulkAddProductForIssue,
  UpdateProductIssue
} from '../interfaces/masters/issue/productIssue';
import axiosClient from '../utils/axios-client';
import { PaginationAndFilter } from '../interfaces/common/pagination';

class IssueApi {
  async getProductIssueByPaginationAndFilter(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/productIssue/getByPaginationAndFilter', options).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getIssuesForProduct(productId: string):
  Promise<{ responseData: IssueModelShort[] | any, statusCode: number }> {
    try {
      const res = await axiosClient.get(`master/productIssue/issue/getById/${productId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async removeIssueFromProduct(issueId: string, productId: string):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const options = {
        issueId,
        productId
      };
      const res = await axiosClient.delete('master/productIssue', { data: options }).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async addIssue(addIssue: AddIssue):
  Promise<{ responseData: Issue | any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/issue', addIssue).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async updateIssue(updateIssue: UpdateIssue):
  Promise<{ responseData: any, statusCode: number }> {
    try {
      const res = await axiosClient.put('master/issue', updateIssue).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async addProductForIssue(object: AddProductIssue):
  Promise<{ responseData: ProductIssue | any, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/productIssue', object).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getIssuesByOptions(options: object):
  Promise<{ responseData: Issue[] | any, statusCode: number }> {
    try {
      const res = await axiosClient.get('master/issue/getByOptions', { params: options }).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getAllIssues(): Promise<{ responseData: Issue[] | any, statusCode: number }> {
    try {
      const res = await axiosClient.get('master/issue/').then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getProductByIssueId(issueId: string):
  Promise<{ responseData: ProductIssueMasterView[] | any, statusCode: number }> {
    try {
      const res = await axiosClient.get(`master/productIssue/product/getById/${issueId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async bulkAddProductForIssue(object: BulkAddProductForIssue):
  Promise<{ responseData: string, statusCode: number }> {
    try {
      const res = await axiosClient.post('master/productIssue/bulkAddProductsforIssue', object).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getIssueById(issueId: string):
  Promise<{ responseData: Issue | any, statusCode: number }> {
    try {
      const res = await axiosClient.get(`master/issue/getById/${issueId}`).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async updateProductIssueStatus(object: UpdateProductIssue):
  Promise<{ responseData: Issue | any, statusCode: number }> {
    try {
      const res = await axiosClient.put('master/productIssue/', object).then((response) => response);
      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      console.log(error);
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }

  async getIssuesFilterData():Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/productIssue/filtersData');

    return response;
  }
}

export const issueApi = new IssueApi();
